/**
 * Encrypted persistence for the account's signing credentials + the
 * minimal auth context the boot path needs to skip the login prompt
 * across a PWA-update reload.
 *
 * The persistence layer pairs two WebCrypto + IndexedDB primitives:
 *
 *   - **Non-extractable AES-GCM CryptoKey** generated at first login
 *     (`generateKey({name:"AES-GCM", length:256}, extractable=false,
 *     ["encrypt","decrypt"])`). The browser prevents
 *     `subtle.exportKey()` from ever returning the raw bytes, so a heap
 *     scrape / disk read never recovers the wrapping key. IndexedDB
 *     persists the opaque handle via `structuredClone` semantics — the
 *     underlying material stays inside the browser's key store.
 *   - **AES-GCM-wrapped payload**. A CREDENTIAL ARRAY (every signing
 *     credential the account holds: the genesis ed25519 seed + the
 *     optional ml-dsa SEED) + the minimal auth context ride together
 *     inside a SINGLE AES-GCM ciphertext keyed on the non-extractable
 *     handle. A page reload restores the handle, decrypts the payload,
 *     and calls `auth_store.restoreSession(...)` — no password prompt,
 *     no `/accounts/login` round-trip.
 *
 * ## v2 credential-array format (credential-lifecycle Hybrid)
 *
 * The decrypted JSON plaintext is versioned. v2 carries a `credentials`
 * array — the SOLE key store — instead of a lone `seedHex`:
 *
 *   {
 *     v: 2,
 *     username, accountId, playerIdHex, locale, csrfToken,
 *     credentials: [
 *       { kind: "ed25519", epoch: 0, seedHex: <32B hex> },   // genesis anchor
 *       { kind: "ml_dsa",  epoch: <u32>, seedHex: <32B hex> } // hybrid only
 *     ]
 *   }
 *
 * RULES (cryptographer lock-in):
 *   (a) SEED-ONLY. The ml-dsa material is its 32-BYTE SEED, NEVER the
 *       ~4032 B `secretKey`. The secretKey is re-derived per session via
 *       `ml_dsa65.keygen(seed)` (see `crypto/ml_dsa.ts`) and dropped
 *       immediately. The ed25519 credential is the raw 32 B seed as
 *       before. No raw key material outside the wrapped blob; nothing is
 *       logged or placed in an error message.
 *   (b) The `credentials` array is the only key store — the legacy flat
 *       `seedHex` field is NOT carried in v2 (migration copies it INTO
 *       the array).
 *   (c) `epoch` is a CLAIM the BE reconciles against its DB row. We store
 *       the BE-reported current epoch when known, else 0 — a wrong epoch
 *       surfaces as `CredentialClaimMismatch` (recoverable by a refetch),
 *       NOT key loss.
 *
 * ## Lossless, idempotent, partial-run-safe migration
 *
 * Existing users have the OLD single-ed25519 record (no `v` tag). On boot
 * `restoreSession()` detects the shape by the `v` tag and migrates the
 * legacy row into the v2 array WITHOUT losing the key:
 *   - The plaintext transform is the pure `migratePlaintextToV2` — it is
 *     unit-tested in-memory (the IDB write/partial-run is covered
 *     separately under fake-indexeddb).
 *   - The decrypted v2 object is returned from the IN-MEMORY transform
 *     regardless of whether the re-encrypt + write committed, so a failed
 *     write still authenticates THIS session; the next boot re-migrates
 *     (the keyless-strand guard: the ed25519 seed is always materialized
 *     in memory from whichever shape decoded).
 *   - The write OVERWRITES `ROW_ID` in place inside a single atomic IDB
 *     `readwrite` tx; the legacy row is never deleted before the put, so
 *     a crash leaves EITHER the old legacy row (re-migrated next boot) OR
 *     the new v2 row — never a torn/empty row.
 *   - Re-encryption uses the SAME persisted non-extractable key (works on
 *     the opaque handle), never a fresh `generateKey` mid-migration.
 *
 * Fallback path: every operation guards `typeof indexedDB`,
 * `globalThis.crypto?.subtle`, and the standard "DOMException" branches
 * (private / incognito mode disables IDB). On a miss the helper returns
 * `null` / `false` so the boot path falls through to the legacy password
 * prompt without throwing. A decrypt failure / non-32-byte ed25519 seed
 * returns `null` (no throw) — same graceful fall-through as before.
 */
const DB_NAME = "arkhe-auth";
/** Bumped 1 -> 2 for the credential-array format. `onupgradeneeded` is a
 *  no-op since the store is keyless `put` by `ROW_ID`, but the bump gives
 *  future migrations a versioned hook. */
const DB_VERSION = 2;
const STORE_NAME = "session";
const ROW_ID = "default";
const IV_BYTES = 12;

/** Current plaintext schema version. */
export const IDB_SCHEMA_VERSION = 2;

export type CredentialKindHex = "ed25519" | "ml_dsa";

/** One persisted signing credential — the 32-byte SEED + its claimed
 *  epoch + kind. For ml-dsa this is the 32 B seed, NOT the secretKey. */
export interface PersistedCredential {
  readonly kind: CredentialKindHex;
  readonly epoch: number;
  /** Lowercase hex of the 32-byte seed. */
  readonly seedHex: string;
}

interface PersistedRow {
  readonly key: CryptoKey;
  readonly ciphertext: ArrayBuffer;
  readonly iv: Uint8Array<ArrayBuffer>;
}

/** v2 decrypted plaintext shape — the credential array + the context the
 *  boot path needs. */
interface PlaintextV2 {
  readonly v: 2;
  readonly username: string;
  readonly accountId: number;
  readonly playerIdHex: string;
  readonly locale: string | null;
  readonly csrfToken: string;
  readonly credentials: ReadonlyArray<PersistedCredential>;
}

/** Legacy (pre-v) decrypted plaintext shape — the single flat ed25519
 *  seed. Migrated into a v2 credential array on the next boot. */
interface PlaintextLegacy {
  readonly seedHex: string;
  readonly username: string;
  readonly accountId: number;
  readonly playerIdHex: string;
  readonly locale: string | null;
  readonly csrfToken: string;
  readonly v?: undefined;
}

/**
 * Decrypted boot-rehydrate payload — matches the
 * `auth_store.restoreSession(...)` argument shape. `signingKey` is the
 * ed25519 seed (kept for back-compat with the auth slice + every existing
 * signing path). `mlDsaSeed` is the optional ml-dsa 32 B SEED (present
 * only for a hybrid account); `mlDsaEpoch` is its claimed epoch.
 * `ed25519Epoch` is the genesis/rotated ed25519 credential epoch (0 at
 * genesis) — the signer keys the auth epoch-trailer off THIS value, never
 * off `mlDsaEpoch`.
 */
export interface RestoredSession {
  readonly signingKey: Uint8Array;
  readonly ed25519Epoch: number;
  readonly mlDsaSeed: Uint8Array | null;
  readonly mlDsaEpoch: number | null;
  readonly username: string;
  readonly accountId: number;
  readonly playerIdHex: string;
  readonly locale: string | null;
  readonly csrfToken: string;
}

/** Login-path persist input. The caller supplies the ed25519 seed (the
 *  genesis credential) + the optional ml-dsa seed for a hybrid account. */
export interface PersistedSessionInput {
  readonly signingKey: Uint8Array;
  readonly ed25519Epoch?: number;
  readonly mlDsaSeed?: Uint8Array | null;
  readonly mlDsaEpoch?: number | null;
  readonly username: string;
  readonly accountId: number;
  readonly playerIdHex: string;
  readonly locale: string | null;
  readonly csrfToken: string;
}

function idbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

function subtleAvailable(): boolean {
  return (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.subtle !== "undefined"
  );
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("idb open failed"));
  });
}

function readRow(db: IDBDatabase): Promise<PersistedRow | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(ROW_ID);
    req.onsuccess = () => {
      const v = req.result as PersistedRow | undefined;
      resolve(v ?? null);
    };
    req.onerror = () => reject(req.error ?? new Error("idb read failed"));
  });
}

function writeRow(db: IDBDatabase, row: PersistedRow): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.put(row, ROW_ID);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("idb write failed"));
    tx.onabort = () => reject(tx.error ?? new Error("idb write aborted"));
  });
}

function deleteRow(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.delete(ROW_ID);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("idb delete failed"));
  });
}

function bytesToHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) {
    out += bytes[i]!.toString(16).padStart(2, "0");
  }
  return out;
}

function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error("hex string must be even length");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * Pure plaintext transform: a decoded legacy OR v2 plaintext object ->
 * the canonical v2 shape. Exported so the lossless / idempotent migration
 * is unit-testable in-memory without an IDB round-trip.
 *
 * - Legacy (`v` absent): builds `credentials = [{ed25519, epoch 0,
 *   seedHex}]` carrying ALL legacy context verbatim. This is the
 *   keyless-strand guard — the ed25519 seed is preserved byte-for-byte.
 * - v2: returned unchanged (idempotent short-circuit).
 *
 * Returns `null` for an unrecognized / malformed shape (the caller falls
 * through to the password prompt). Never throws.
 */
export function migratePlaintextToV2(input: unknown): PlaintextV2 | null {
  if (input === null || typeof input !== "object") return null;
  const obj = input as Record<string, unknown>;

  // v2 shape — idempotent: validate the array minimally + pass through.
  if (obj.v === 2) {
    const creds = obj.credentials;
    if (!Array.isArray(creds)) return null;
    const credentials: PersistedCredential[] = [];
    for (const c of creds) {
      if (c === null || typeof c !== "object") return null;
      const cc = c as Record<string, unknown>;
      if (cc.kind !== "ed25519" && cc.kind !== "ml_dsa") return null;
      if (typeof cc.seedHex !== "string") return null;
      const epoch = typeof cc.epoch === "number" ? cc.epoch : 0;
      credentials.push({ kind: cc.kind, epoch, seedHex: cc.seedHex });
    }
    if (
      typeof obj.username !== "string" ||
      typeof obj.accountId !== "number" ||
      typeof obj.playerIdHex !== "string" ||
      typeof obj.csrfToken !== "string"
    ) {
      return null;
    }
    return {
      v: 2,
      username: obj.username,
      accountId: obj.accountId,
      playerIdHex: obj.playerIdHex,
      locale: typeof obj.locale === "string" ? obj.locale : null,
      csrfToken: obj.csrfToken,
      credentials,
    };
  }

  // Legacy shape — flat seedHex => credential array.
  if (typeof obj.seedHex !== "string") return null;
  if (
    typeof obj.username !== "string" ||
    typeof obj.accountId !== "number" ||
    typeof obj.playerIdHex !== "string" ||
    typeof obj.csrfToken !== "string"
  ) {
    return null;
  }
  const legacy = obj as unknown as PlaintextLegacy;
  return {
    v: 2,
    username: legacy.username,
    accountId: legacy.accountId,
    playerIdHex: legacy.playerIdHex,
    locale: typeof legacy.locale === "string" ? legacy.locale : null,
    csrfToken: legacy.csrfToken,
    credentials: [{ kind: "ed25519", epoch: 0, seedHex: legacy.seedHex }],
  };
}

/** Build a `RestoredSession` from an in-memory v2 plaintext object.
 *  Returns `null` when the ed25519 genesis credential is missing or its
 *  seed is not 32 bytes (defense-in-depth against a corrupted row). */
function v2ToRestoredSession(p: PlaintextV2): RestoredSession | null {
  const ed = p.credentials.find((c) => c.kind === "ed25519");
  if (ed === undefined) return null;
  let signingKey: Uint8Array;
  try {
    signingKey = hexToBytes(ed.seedHex);
  } catch {
    return null;
  }
  if (signingKey.length !== 32) return null;

  const ml = p.credentials.find((c) => c.kind === "ml_dsa");
  let mlDsaSeed: Uint8Array | null = null;
  let mlDsaEpoch: number | null = null;
  if (ml !== undefined) {
    let seed: Uint8Array;
    try {
      seed = hexToBytes(ml.seedHex);
    } catch {
      return null;
    }
    if (seed.length !== 32) return null;
    mlDsaSeed = seed;
    mlDsaEpoch = ml.epoch;
  }

  return {
    signingKey,
    ed25519Epoch: ed.epoch,
    mlDsaSeed,
    mlDsaEpoch,
    username: p.username,
    accountId: p.accountId,
    playerIdHex: p.playerIdHex,
    locale: p.locale,
    csrfToken: p.csrfToken,
  };
}

/** Serialize a v2 plaintext into the JSON string the AES-GCM ciphertext
 *  wraps. Centralised so the persist + migrate paths emit identical
 *  bytes. */
function serializeV2(p: PlaintextV2): string {
  return JSON.stringify(p);
}

async function encryptUnderKey(
  key: CryptoKey,
  plaintext: string,
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array<ArrayBuffer> }> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  return { ciphertext, iv };
}

/**
 * Encrypt + persist the boot-rehydrate payload (login path). Writes the
 * v2 credential-array shape directly under a FRESH non-extractable
 * AES-GCM key. Fire-and-forget; resolves `false` on any platform issue
 * (private mode / IDB blocked / subtle missing) and `true` once the
 * ciphertext + handle hit disk.
 */
export async function persistSession(
  input: PersistedSessionInput,
): Promise<boolean> {
  if (!idbAvailable() || !subtleAvailable()) return false;
  try {
    const key = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
    const credentials: PersistedCredential[] = [
      {
        kind: "ed25519",
        epoch: input.ed25519Epoch ?? 0,
        seedHex: bytesToHex(input.signingKey),
      },
    ];
    if (input.mlDsaSeed != null) {
      credentials.push({
        kind: "ml_dsa",
        epoch: input.mlDsaEpoch ?? 0,
        seedHex: bytesToHex(input.mlDsaSeed),
      });
    }
    const plaintext = serializeV2({
      v: 2,
      username: input.username,
      accountId: input.accountId,
      playerIdHex: input.playerIdHex,
      locale: input.locale,
      csrfToken: input.csrfToken,
      credentials,
    });
    const { ciphertext, iv } = await encryptUnderKey(key, plaintext);
    const db = await openDb();
    try {
      await writeRow(db, { key, ciphertext, iv });
    } finally {
      db.close();
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Add (or replace) a credential of `kind` in the persisted v2 array,
 * re-encrypting under the SAME wrap key. Used by the Tier-2 add-credential
 * recovery/enroll path so subsequent signing emits both legs. Returns
 * `false` (graceful) when there is no decryptable row or the platform
 * lacks the primitives — the caller still has the in-memory seed.
 */
export async function addPersistedCredential(
  kind: CredentialKindHex,
  epoch: number,
  seed: Uint8Array,
): Promise<boolean> {
  if (!idbAvailable() || !subtleAvailable()) return false;
  if (seed.length !== 32) return false;
  try {
    const db = await openDb();
    try {
      const row = await readRow(db);
      if (row === null) return false;
      const plaintext = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: row.iv },
        row.key,
        row.ciphertext,
      );
      const decoded = JSON.parse(
        new TextDecoder().decode(plaintext),
      ) as unknown;
      const v2 = migratePlaintextToV2(decoded);
      if (v2 === null) return false;
      const next: PersistedCredential[] = v2.credentials.filter(
        (c) => c.kind !== kind,
      );
      next.push({ kind, epoch, seedHex: bytesToHex(seed) });
      const { ciphertext, iv } = await encryptUnderKey(
        row.key,
        serializeV2({
          ...v2,
          credentials: next,
        }),
      );
      // Re-use the SAME persisted non-extractable key handle.
      await writeRow(db, { key: row.key, ciphertext, iv });
      return true;
    } finally {
      db.close();
    }
  } catch {
    return false;
  }
}

/**
 * Restore the persisted boot-rehydrate payload, migrating a legacy
 * single-ed25519 row into the v2 credential array on the fly. Returns
 * `null` when no row exists, the row decrypt fails, the shape is
 * unrecognized, or the platform doesn't support the primitives. The
 * caller treats `null` as "fall through to the normal login flow" — no
 * error toast, no surface.
 *
 * Migration is LOSSLESS + IDEMPOTENT + PARTIAL-RUN-SAFE: the in-memory v2
 * object is derived (and returned) BEFORE the re-encrypt + write, so a
 * failed write still authenticates this session; the next boot re-runs the
 * (idempotent) migration. A v2 row short-circuits the rewrite.
 */
export async function restoreSession(): Promise<RestoredSession | null> {
  if (!idbAvailable() || !subtleAvailable()) return null;
  try {
    const db = await openDb();
    try {
      const row = await readRow(db);
      if (row === null) return null;
      const plaintextBytes = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: row.iv },
        row.key,
        row.ciphertext,
      );
      const decoded = JSON.parse(
        new TextDecoder().decode(plaintextBytes),
      ) as unknown;
      const wasV2 =
        decoded !== null &&
        typeof decoded === "object" &&
        (decoded as Record<string, unknown>).v === 2;
      const v2 = migratePlaintextToV2(decoded);
      if (v2 === null) return null;
      const restored = v2ToRestoredSession(v2);
      if (restored === null) return null;

      // Persist the migrated v2 shape ONLY when we migrated a legacy row.
      // The in-memory `restored` is already correct; the write is a
      // best-effort disk update that a failed/aborted tx leaves
      // re-migratable next boot (the legacy row is never deleted). Re-use
      // the SAME persisted key handle — never a fresh generateKey, or the
      // ciphertext would become undecryptable.
      if (!wasV2) {
        try {
          const { ciphertext, iv } = await encryptUnderKey(
            row.key,
            serializeV2(v2),
          );
          await writeRow(db, { key: row.key, ciphertext, iv });
        } catch {
          // Partial-run-safe: keep the legacy row in place; this session
          // authenticates from the in-memory `restored`, next boot
          // re-migrates.
        }
      }
      return restored;
    } finally {
      db.close();
    }
  } catch {
    return null;
  }
}

/**
 * Drop the persisted row + the AES-GCM handle. Called from the logout
 * sequence + on a 401 / `markSessionRevoked` so a revoked session can
 * never re-hydrate.
 */
export async function clearPersistedSession(): Promise<void> {
  if (!idbAvailable()) return;
  try {
    const db = await openDb();
    try {
      await deleteRow(db);
    } finally {
      db.close();
    }
  } catch {
    // Swallow — failed clear is no worse than the current model (in-memory
    // seed already gone via the caller's zeroize).
  }
}
